import React, { useEffect, useState, useCallback } from "react";
import { View, StyleSheet } from "react-native";
import WebView from "react-native-webview";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import { useRoute, useFocusEffect, RouteProp } from "@react-navigation/native";
import CustomHtml from "../components/common/general/CustomHtml/CustomHtml";
import RoundedLabel from "../components/common/label/RoundedLabel/RoundedLabel";
import { File, FileStatus, StoragePathType, useSubmitStudentAssignmentMutation } from "../graphql/generated/graphql";
import Skeleton from "react-loading-skeleton";
import { useAppState } from "../contexts/AppStateContext";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import NewCustomAxioActionModal from "../containers/settings/UsersTabContainer/NewCustomAxioActionModal";
import { shadowStyles } from "../styles/shadow";
import DefaultLayout from "../layouts/DefaultLayout.web";
import { ResourceType, useActiveCourse } from "../contexts/ActiveCourseContext";
import { getEmbeddedVideoUrl } from "../utils/regex.dictionary";
import StudentFileUpload from "../components/customCoursework/StudentFileUpload/StudentFileUpload";
import ResourceCard from "../components/customCoursework/StudentFileUpload/ResourceCard";

// Grade display component
const GradeDisplay = ({ grade, feedback }) => {
  if (grade === null || grade === undefined) {
    return null;
  }

  return (
    <View style={styles.sectionContainer}>
      <CustomText text="Assignment Grade" size="m" weight="ultraBold" useTranslationText={false} />
      <CustomSpacing type="vertical" size="m" />
      <View style={styles.gradeContainer}>
        <RoundedLabel
          text={`${grade}/100`}
          style={styles.gradedLabel}
          textStyle={styles.gradedLabelText}
          padding={[12, 8]}
          isRounded={true}
        />
      </View>

      {feedback && (
        <>
          <CustomSpacing type="vertical" size="m" />
          <CustomText text="Teacher Feedback:" size="s" weight="bold" useTranslationText={false} />
          <CustomSpacing type="vertical" size="xs" />
          <View style={styles.feedbackContainer}>
            <CustomText text={feedback} size="s" useTranslationText={false} style={styles.feedbackText} />
          </View>
        </>
      )}
    </View>
  );
};

function CustomAssignmentStudentView() {
  const route = useRoute<RouteProp<RootStackParamList, "Custom Assignment Student">>();
  const { resource_type: resourceType, resource_id: resourceId } = route.params;

  const [submitStudentAssignment] = useSubmitStudentAssignmentMutation();
  const [submissionConfirmationModalVisible, setSubmissionConfirmationModalVisible] = useState(false);

  const { dispatch } = useAppState();

  const {
    assignmentData,
    loadingAssignment,
    setActiveResource,
    activeResource,
    refetchUserSubmissions,
    userSubmissionsData,
  } = useActiveCourse();

  useEffect(() => {
    setActiveResource({ resourceType: resourceType as ResourceType, resourceId });
  }, [resourceType, resourceId]);

  const [files, setFiles] = useState<Partial<File>[]>([]);

  useEffect(() => {
    setFiles([]);
    refetchUserSubmissions();
  }, [refetchUserSubmissions, activeResource?.resourceId]);

  useEffect(() => {
    if (userSubmissionsData?.getUserSubmissions?.data?.[0]?.files) {
      setFiles(userSubmissionsData?.getUserSubmissions?.data?.[0]?.files);
    }
  }, [userSubmissionsData]);

  // Helper functions for grade display
  const hasGrade = () => {
    // First check if we have a submission
    if (!userSubmissionsData?.getUserSubmissions?.data?.[0]) {
      return false;
    }

    // Then check if it has been graded
    const submission = userSubmissionsData.getUserSubmissions.data[0];
    return submission.hasTeacherGraded && submission.grade !== null && submission.grade !== undefined;
  };

  const getSubmissionGrade = () => {
    if (!hasGrade()) {
      return null;
    }
    return userSubmissionsData.getUserSubmissions.data[0].grade;
  };

  const getSubmissionFeedback = () => {
    if (!hasGrade()) {
      return null;
    }
    return userSubmissionsData.getUserSubmissions.data[0].feedback;
  };

  useFocusEffect(
    useCallback(() => {
      dispatch({
        type: "SET_MEDIA",
        payload: {
          video: null,
          wiki: null,
        },
      });

      return () => {
        dispatch({
          type: "SET_MEDIA",
          payload: {
            video: null,
            wiki: null,
          },
        });
      };
    }, [dispatch])
  );

  const handleSubmitAssignment = async () => {
    const pendingFiles = files.filter((file) => file.status === FileStatus.Pending);
    if (pendingFiles.length === 0) {
      return;
    }
    await submitStudentAssignment({
      variables: {
        assignmentId: activeResource?.resourceId,
        files: pendingFiles.map((file) => ({ fileId: file.id, fileName: file.fileName, fileURL: file.fileURL })),
      },
    });
    setSubmissionConfirmationModalVisible(true);
  };

  const handleCloseSubmissionModal = () => {
    setSubmissionConfirmationModalVisible(false);
    refetchUserSubmissions();
  };

  const renderSubmittedAssignments = () => {
    // Filter to only show submitted files (not pending)
    const submittedFiles = files.filter((file) => file.status !== FileStatus.Pending);

    if (!submittedFiles || submittedFiles.length === 0) {
      return (
        <View style={styles.noSubmissionsContainer}>
          <CustomText
            text="Looks like you haven't submitted anything for this assignment."
            size="m"
            useTranslationText={false}
            style={{ justifyContent: "center", textAlign: "center" }}
          />
        </View>
      );
    }

    return (
      <View style={styles.filesContainer}>
        {submittedFiles.map((file) => (
          <ResourceCard key={file.id} file={file} showDate={true} />
        ))}
      </View>
    );
  };

  const renderSkeletonLoader = () => (
    <View style={styles.skeletonContainer}>
      <View style={styles.detailsContainer}>
        <View style={styles.headerContainer}>
          <Skeleton width={40} height={40} circle={true} />
          <CustomSpacing type="horizontal" size="m" />
          <Skeleton width="60%" height={24} />
        </View>
        <CustomSpacing type="vertical" size="m" />
        <CustomSpacing type="vertical" size="l" />
        <Skeleton width="30%" height={40} borderRadius={8} />
        <CustomSpacing type="vertical" size="l" />
        <Skeleton width="100%" height={200} borderRadius={12} />
      </View>
    </View>
  );

  const AssignmentVideoPlayer = ({ videoId }) => {
    return (
      <View style={styles.fullWidthVideoContainer}>
        <WebView
          style={styles.webView}
          source={{
            uri: getEmbeddedVideoUrl(videoId),
          }}
          javaScriptEnabled={true}
          allowsFullscreenVideo={true}
          domStorageEnabled={true}
        />
      </View>
    );
  };

  const renderMainContent = () => {
    if (loadingAssignment) {
      return renderSkeletonLoader();
    }

    return (
      <View style={styles.contentRow}>
        <View style={styles.leftColumn}>
          <CustomSpacing type="vertical" size="l" />

          {assignmentData?.getAssignment?.data?.[0]?.description && (
            <View style={styles.descriptionContainer}>
              <CustomText text="Instructions:" size="m" weight="ultraBold" useTranslationText={false} />
              <CustomSpacing type="vertical" size="s" />
              <CustomHtml html={assignmentData.getAssignment.data[0].description} />
            </View>
          )}

          {assignmentData?.getAssignment?.data?.[0]?.videoUrl && (
            <View style={styles.sectionContainer}>
              <CustomText text="Video" size="m" weight="ultraBold" useTranslationText={false} />
              <CustomSpacing type="vertical" size="s" />
              {assignmentData?.getAssignment?.data?.[0]?.videoUrl && (
                <AssignmentVideoPlayer
                  videoId={getEmbeddedVideoUrl(assignmentData?.getAssignment?.data?.[0]?.videoUrl)}
                />
              )}
            </View>
          )}

          {assignmentData?.getAssignment?.data?.[0]?.worksheet && (
            <View style={styles.sectionContainer}>
              <CustomText text="Worksheet" size="m" weight="ultraBold" useTranslationText={false} />
              <CustomSpacing type="vertical" size="s" />
              {assignmentData?.getAssignment?.data?.[0]?.worksheet?.files?.length > 0 && (
                <>
                  <View style={styles.filesContainer}>
                    {assignmentData?.getAssignment?.data?.[0]?.worksheet?.files.map((file) => (
                      <ResourceCard key={file.id} file={file} showDate={false} />
                    ))}
                  </View>
                  <CustomSpacing type="vertical" size="m" />
                </>
              )}
            </View>
          )}
        </View>

        <View style={styles.rightColumn}>
          {hasGrade() ? (
            <GradeDisplay grade={getSubmissionGrade()} feedback={getSubmissionFeedback()} />
          ) : (
            <>
              {assignmentData?.getAssignment?.data?.[0]?.files?.length > 0 && (
                <View style={styles.sectionContainer}>
                  <CustomText text="Assignment Resources" size="m" weight="ultraBold" useTranslationText={false} />
                  <CustomSpacing type="vertical" size="s" />
                  <View style={styles.filesContainer}>
                    {assignmentData.getAssignment.data[0].files.map((file) => (
                      <ResourceCard key={file.id} file={file} showDate={false} />
                    ))}
                  </View>
                </View>
              )}

              {assignmentData?.getAssignment?.data?.[0]?.enableStudentFileUpload && !hasGrade() && (
                <View style={styles.sectionContainer}>
                  <CustomText text="Upload Assignment" size="m" weight="ultraBold" useTranslationText={false} />
                  <CustomSpacing type="vertical" size="m" />

                  {!userSubmissionsData?.getUserSubmissions?.data?.[0]?.isCompleted ? (
                    <StudentFileUpload
                      resource={activeResource}
                      filesState={[files, setFiles]}
                      storagePath={StoragePathType.Resources}
                      onSubmitFiles={handleSubmitAssignment}
                    />
                  ) : (
                    <>
                      <CustomText
                        text="You have already submitted this assignment. Your submission is being reviewed."
                        size="s"
                        style={styles.submissionInfoText}
                        useTranslationText={false}
                      />
                    </>
                  )}
                </View>
              )}

              <View style={styles.sectionContainer}>
                <CustomText text="Submitted Assignments" size="m" weight="ultraBold" useTranslationText={false} />
                <CustomSpacing type="vertical" size="m" />
                {renderSubmittedAssignments()}
              </View>
            </>
          )}
        </View>
      </View>
    );
  };

  return (
    <DefaultLayout showCourseNav={true} pageHasChat={false}>
      {renderMainContent()}

      <NewCustomAxioActionModal
        visible={submissionConfirmationModalVisible}
        onClose={handleCloseSubmissionModal}
        modalTexts={{
          title: "Submission Confirmation",
          warningText: "You have successfully submitted your assignment.",
          ariaLabel: "Submission Confirmation",
          actionButtonText: "Close",
        }}
      />
    </DefaultLayout>
  );
}

const styles = StyleSheet.create({
  gradedLabel: {
    backgroundColor: "#ECFDF3",
    borderRadius: 360,
  },
  gradedLabelText: {
    color: "#027A48",
    fontSize: 12,
    fontWeight: "700",
  },
  contentRow: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  leftColumn: {
    width: "74%",
    paddingRight: 40,
    minWidth: 300,
  },
  rightColumn: {
    width: "25%",
    minWidth: 250,
  },
  descriptionContainer: {
    marginBottom: 20,
  },
  sectionContainer: {
    marginBottom: 30,
  },
  submissionInfoText: {
    color: "#6B7280",
  },
  filesContainer: {
    marginBottom: 10,
    flexDirection: "column",
    gap: 10,
    width: "100%",
  },
  noSubmissionsContainer: {
    padding: 16,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    marginBottom: 20,
  },
  skeletonContainer: {
    flex: 1,
    padding: 20,
  },
  detailsContainer: {
    flex: 1,
  },
  headerContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
  },
  fullWidthVideoContainer: {
    width: "100%",
    aspectRatio: 16 / 9,
    marginBottom: 20,
    overflow: "hidden",
    borderRadius: 8,
    ...shadowStyles.smallShadow,
  },
  webView: {
    width: "100%",
    height: "100%",
  },
  gradeContainer: {
    marginVertical: 8,
    alignItems: "flex-start",
  },
  feedbackContainer: {
    padding: 12,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  feedbackText: {
    color: "#374151",
    lineHeight: 20,
  },
});

export default CustomAssignmentStudentView;
